import React, {useEffect, useMemo, useState} from "react";
import styles from "./styles.module.scss";

const APPLY_URL = "https://open.xiniudata.com/";
const FEISHU_DOC_URL = "https://xiniushuju.feishu.cn/docx/UJBVdNeGwo8bEXxktZ8cypUGnlb";
const SECTION_IDS = {
  intro: "mcp-intro",
  core: "mcp-core",
  data: "mcp-data",
  scenarios: "mcp-scenarios",
  start: "mcp-start"
} as const;

export default function MCP() {
  const [activeTab, setActiveTab] = useState<string>(SECTION_IDS.core);

  const openApply = () => {
    window.location.href = APPLY_URL;
  };

  const openDoc = () => {
    window.open(FEISHU_DOC_URL, "_blank", "noopener,noreferrer");
  };

  const tabs = useMemo(() => ([
    // {id: SECTION_IDS.intro, label: "首页"},
    {id: SECTION_IDS.core, label: "功能特性"},
    {id: SECTION_IDS.data, label: "数据能力"},
    {id: SECTION_IDS.scenarios, label: "使用场景"},
    {id: SECTION_IDS.start, label: "申请接入"}
  ]), []);

  useEffect(() => {
    const sectionIds = tabs.map((item) => item.id);
    const lastSectionId = sectionIds[sectionIds.length - 1];

    const onScroll = () => {
      const doc = document.documentElement;
      const viewportBottom = window.scrollY + window.innerHeight;
      const isNearBottom = viewportBottom >= doc.scrollHeight - 4;

      if (isNearBottom) {
        setActiveTab(lastSectionId);
        return;
      }

      const checkpoint = window.scrollY + 140;
      let currentId = sectionIds[0];
      for (let i = 0; i < sectionIds.length; i += 1) {
        const section = document.getElementById(sectionIds[i]);
        if (section && section.offsetTop <= checkpoint) {
          currentId = sectionIds[i];
        }
      }
      setActiveTab(currentId);
    };

    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [tabs]);

  const jumpTo = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return;
    setActiveTab(id);
    const top = element.getBoundingClientRect().top + window.scrollY - 60;
    window.scrollTo({top, behavior: "smooth"});
  };

  const skills = [
    {title: "公司基本信息查询", desc: "通过项目公司表查询企业工商全称、成立时间、行业分类等基础信息", icon: "/static-official/images/mcp_core_mcp_blue1.png"},
    {title: "投资机构信息查询", desc: "获取投资机构的成立时间、投资阶段偏好、投资偏好等详细信息", icon: "/static-official/images/mcp_core_mcp_blue2.png"},
    {title: "基金信息查询", desc: "查询基金名称、成立时间、管理规模、投资方向等基金基础信息", icon: "/static-official/images/mcp_core_mcp_purple.png"},
    {title: "基金管理人信息查询", desc: "获取GP管理经验、投资业绩、管理基金列表等专业信息", icon: "/static-official/images/mcp_core_mcp_blue1.png"},
    {title: "LP实体信息查询", desc: "查询LP实体类型、投资偏好、出资历史等LP相关基础信息", icon: "/static-official/images/mcp_core_mcp_blue2.png"},
    {title: "结构化数据查询接口", desc: "烯牛创投数据MCP提供了一整套结构化数据查询工具，支持从表结构探索到复杂关联查询的完整数据分析流程。", icon: "/static-official/images/mcp_core_mcp_purple.png"}
  ];

  const scenarioCards = [
    { title: "投资机构尽职调查", desc: "快速生成目标机构的全面尽调报告，包括基本信息、投资业绩、团队背景等多维度分析。", image: "mcp_use_01" },
    { title: "基金绩效分析", desc: "深度分析基金的历史业绩、投资回报、风险指标，为LP投资决策提供数据支撑。", image: "mcp_use_02" },
    { title: "行业趋势研究", desc: "基于海量投融资数据，洞察行业热点、投资趋势，把握市场先机。", image: "mcp_use_03" },
    { title: "地区投资热度分析", desc: "分析各地区的投资活跃度、项目分布、资本流向，辅助区域投资策略制定。", image: "mcp_use_04" },
    { title: "项目筛选与评估", desc: "从海量项目库中智能筛选符合投资策略的标的，自动提取关键指标，快速完成初筛与优先级排序，提升项目发现效率。", image: "mcp_use_05" },
    { title: "投后管理追踪", desc: "持续监控已投项目的经营数据、融资动态与市场表现，及时预警异常变化，为投后管理与退出决策提供实时数据支持。", image: "mcp_use_06" }
  ];

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <a href="/#/">
            <img src="/static-official/images/mcp_header_logo.png" alt="烯牛数据" className={styles.logo}/>
          </a>
          <div className={styles.tabs}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ""}`}
                onClick={() => jumpTo(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <button className={styles.headerApplyBtn} onClick={openApply}>申请接入</button>
        </div>
      </header>

      <section className={styles.hero} id={SECTION_IDS.core}>
        <div className={styles.heroInner}>
          <div className={styles.heroPanel}>
            <picture className={styles.heroBgWrap}>
              <source srcSet="/static-official/images/mcp_desc_bg.webp" type="image/webp"/>
              <img className={styles.heroBg} src="/static-official/images/mcp_desc_bg.png" alt="MCP 背景图"/>
            </picture>
            <div className={styles.heroOverlay}>
              <h1>烯牛数据 MCP</h1>
              <p>通过标准化接口，让 AI 大模型直接调用烯牛数据的创投数据库， 实现智能化的投资数据分析与决策支持</p>
            </div>
            <div className={styles.heroActionWrap}>
              <div className={styles.heroActions}>
                <button className={styles.imageBtn} onClick={openApply}>
                  <img src="/static-official/images/mcp_desc_button_apply.png" alt="立即申请"/>
                </button>
                {/* <button className={styles.imageBtn} onClick={openDoc}>
                  <WebPImg name="mcp_desc_button_doc" alt="查看文档"/>
                </button> */}
              </div>
            </div>
            <div className={styles.mouseWrap}>
              <img className={styles.mouseIcon} src="/static-official/images/mcp_desc_mouse.png" alt="scroll"/>
              <p>下滑深入了解</p>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.core} id={SECTION_IDS.core}>
        <div className={styles.coreInner}>
          <h2>MCP 核心能力</h2>
          <p className={styles.subTitle}>基于 Model Context Protocol 标准，提供丰富的数据查询与分析能力</p>
          <div className={styles.skillGrid}>
          {skills.map((item) => (
            <article className={styles.skillCard} key={item.title}>
              <img className={styles.skillIcon} src={item.icon} alt={item.title}/>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </article>
          ))}
          </div>
        </div>
      </section>

      <section
        className={styles.data}
        id={SECTION_IDS.data}
      >
        <div className={`${styles.sectionInner} ${styles.scenarioInner}`}>
          <h2>数据能力</h2>
          <p className={styles.subTitle}>覆盖全行业的创投数据库，为投资决策提供坚实的数据基础</p>
          <div className={styles.dataGrid}>
            <article>
              <h3>50,000+</h3>
              <p>投资机构库</p>
              <span>洞察VC/PE机构</span>
              <br />
              <span>投资策略与市场动态</span>
            </article>
            <article>
              <h3>100,000+</h3>
              <p>LP 库</p>
              <span>追踪有限合伙人</span>
              <br />
              <span>资本流向与投资偏好</span>
            </article>
            <article>
              <h3>200,000+</h3>
              <p>基金库</p>
              <span>掌握基金</span>
              <br />
              <span>募投管退全周期策略信息</span>
            </article>
            <article>
              <h3>500,000+</h3>
              <p>投资事件库</p>
              <span>分析一级市场</span>
              <br />
              <span>投资趋势与交易脉络</span>
            </article>
            <article>
              <h3>3,000,000+</h3>
              <p>科创企业库</p>
              <span>发现高成长性</span> 
              <br />
              <span>创新企业与投资机会</span>
            </article>
          </div>
        </div>
      </section>

      <section
        className={styles.scenarios}
        id={SECTION_IDS.scenarios}
      >
        <div className={`${styles.sectionInner} ${styles.scenarioInner}`}>
          <h2>使用场景</h2>
          <p className={styles.subTitle}>在创投领域的典型应用场景，助力业务高效运转</p>
          <div className={styles.scenarioGrid}>
            {scenarioCards.map((item) => (
              <article className={styles.scenarioCard} key={item.title}>
                <div className={styles.scenarioText}>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
                <div className={styles.scenarioImageWrap}>
                  <picture>
                  <source srcSet={`/static-official/images/${item.image}.webp`} type="image/webp"/>
                  <img src={`/static-official/images/${item.image}.png`} alt={item.title}/>
                </picture>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        className={styles.start}
        id={SECTION_IDS.start}
        style={{backgroundImage: "url(/static-official/images/mcp_access_bg.webp)"}}
      >
        <div className={styles.sectionInner}>
          <h2>开始接入烯牛数据 MCP</h2>
          <p>立即申请，让 AI 大模型拥有专业的创投数据分析能力</p>
          <div className={styles.heroActions}>
            <button className={styles.imageBtn} onClick={openApply}>
              <img src="/static-official/images/mcp_desc_button_apply.png" alt="立即申请"/>
            </button>
            {/* <button className={styles.imageBtn} onClick={openDoc}>
              <img src="/static-official/images/mcp_desc_button_doc.png" alt="查看文档"/>
            </button> */}
          </div>
        </div>
      </section>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <span>© 2024 烯牛数据. All rights reserved.</span>
          <span>邮箱：hello@xiniudata.com</span>
        </div>
      </footer>
    </section>
  );
}
